import { useEffect } from "react";
import { Link } from "react-router-dom";
import { setPageSeo } from "@/utils/seo";

const brandSuffix = "51每日大赛今日黑聚集地详情";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "聚焦每日大赛",
    desc: "以“每日大赛”为核心，持续追踪当天与近期的重点赛事，让用户第一时间掌握赛程与结果。",
  },
  {
    icon: "ri-stack-line",
    title: "结构化详情",
    desc: "每场赛事均包含基本信息、赛况介绍、赛程安排与数据展示，信息层次清晰、便于查阅。",
  },
  {
    icon: "ri-compass-3-line",
    title: "便捷导航",
    desc: "通过分类、标签、搜索与专题聚合，帮助不同关注方向的用户快速找到想看的内容。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "contact@51daily-arena.example" },
  { icon: "ri-phone-line", label: "合作咨询", value: "+86 021-8888-5151" },
  { icon: "ri-time-line", label: "服务时间", value: "工作日 9:00 - 18:00" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "上海市浦东新区赛事大厦 18 层" },
];

export default function About() {
  useEffect(() => {
    setPageSeo({
      title: `关于我们 - ${brandSuffix}`,
      description:
        "51每日大赛今日黑聚集地详情是面向电竞与体育赛事爱好者的专业资讯平台，提供赛事详情、赛程安排与动态更新，并说明平台定位、联系方式与免责声明。",
      keywords: "关于我们,赛事资讯平台,联系方式,免责声明,每日大赛",
    });
  }, []);

  return (
    <div>
      <section className="relative w-full overflow-hidden">
        <div className="relative h-60 w-full md:h-72">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20editorial%20workspace%20with%20large%20screen%20showing%20sports%20data%20dashboard%2C%20dark%20blue%20and%20orange%20accents%2C%20clean%20minimal%20studio%2C%20professional%20cinematic%20lighting&width=1600&height=800&seq=about-banner&orientation=landscape"
            alt="51每日大赛今日黑聚集地详情平台介绍"
            title="赛事资讯平台介绍"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/70 to-foreground-950/40"></div>
        </div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-[1280px] px-4 pb-8 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-background-300 md:text-sm">
              <Link to="/" className="transition-colors hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-100">关于我们</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
              关于 51每日大赛今日黑聚集地详情
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-background-200 md:text-base">
              专注每日大赛资讯与详情介绍的专业平台，为赛事爱好者提供清晰、权威、易读的赛事内容。
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6">
        <section className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-accent-600">
              <span className="h-1 w-1 rounded-full bg-accent-500"></span>
              平台简介
            </span>
            <h2 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              为赛事爱好者打造的资讯聚合平台
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              51每日大赛今日黑聚集地详情成立于对赛事内容的热爱。我们发现，赛事信息分散在不同渠道，用户往往需要花费大量时间才能拼凑出完整的赛况。因此，我们以“每日大赛”为主线，将赛事分类、详情、赛程与动态更新整合到统一的平台中。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              平台面向电竞及体育赛事爱好者、专业观众、赛事分析师及相关行业人员，兼顾快速浏览与深度阅读的需求，力求让每一个关注赛事的人都能在最短时间内获取所需信息。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {values.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className={`${item.icon} text-2xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <div className="mb-6 flex items-center gap-3">
            <span className="h-6 w-1 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              联系方式
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {contacts.map((contact) => (
              <div
                key={contact.label}
                className="rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${contact.icon} text-xl`}></i>
                </span>
                <p className="mt-4 text-xs text-foreground-500">{contact.label}</p>
                <p className="mt-1 break-all text-sm font-semibold text-foreground-950">
                  {contact.value}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-200 text-secondary-800">
              <i className="ri-shield-check-line text-xl"></i>
            </span>
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              版权及免责声明
            </h2>
          </div>
          <div className="mt-5 space-y-3 text-sm leading-relaxed text-foreground-700">
            <p>
              1. 本站所有赛事名称、时间、地点、参赛队伍及相关数据均为演示用虚构内容，不对应任何真实赛事、机构或个人。
            </p>
            <p>
              2. 站内的搜索、筛选、排序与分页功能均为前端模拟展示，不接入任何真实数据接口，也不提供视频播放或下载功能。
            </p>
            <p>
              3. 本站内容仅供界面展示与功能演示参考，不构成任何赛事预测、投注建议或商业承诺。若需了解真实赛事信息，请以官方发布为准。
            </p>
            <p>4. 未经许可，任何人不得将本站演示内容用于商业用途。</p>
          </div>
          <p className="mt-6 text-xs text-foreground-500">
            © 2026 51每日大赛今日黑聚集地详情 版权所有
          </p>
        </section>
      </div>
    </div>
  );
}